import CoreForm, { IChangeEvent } from "@rjsf/core";
import { CustomValidator, RJSFSchema } from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { filterDataUrlTypeErrors } from "../utils/filter-data-url-type-errors";
import { validateDisabledEnum } from "../utils/validate-disabled-enum";

type IgnoreType = { ["ignore"]: "me" };

interface UseBrostarFormOptions {
  schema: RJSFSchema;
  uiSchema: object | undefined;
  passedCustomValidate: CustomValidator<IgnoreType> | undefined;
  formData: IgnoreType | undefined;
  onChange: ((e: IChangeEvent<IgnoreType>) => void) | undefined;
}

export const useBrostarForm = ({
  schema,
  uiSchema,
  passedCustomValidate,
  formData,
  onChange,
}: UseBrostarFormOptions) => {
  const formRef = useRef<CoreForm<IgnoreType>>(null);

  const customValidate: CustomValidator<IgnoreType> = useCallback(
    (data, errors) => {
      validateDisabledEnum(uiSchema, data, errors);
      passedCustomValidate?.(data, errors);
      return errors;
    },
    [uiSchema, passedCustomValidate]
  );

  const transformErrors = useMemo(
    () => (errors: RJSFSchema["errors"]) => filterDataUrlTypeErrors(errors, schema),
    [schema]
  );

  const getIsValid = useCallback(
    (data: IgnoreType | undefined) => {
      if (data === undefined) return false;

      const { errors } = validator.validateFormData(data, schema, customValidate, transformErrors);

      return errors.length === 0;
    },
    [schema, customValidate, transformErrors]
  );

  const [isValid, setIsValid] = useState(() => getIsValid(formData));

  useEffect(() => {
    setIsValid(getIsValid(formData));
  }, [formData, getIsValid]);

  const handleChange = useCallback(
    (e: IChangeEvent<IgnoreType>) => {
      setIsValid(getIsValid(e.formData));
      onChange?.(e);
    },
    [onChange, getIsValid]
  );

  const onReset = () => {
    if (onChange) {
      return onChange({ formData: undefined } as IChangeEvent<IgnoreType>);
    }
    formRef.current?.reset();
  };

  return { formRef, customValidate, transformErrors, isValid, handleChange, onReset };
};
